import { ArrowRight, ExternalLink, X } from 'lucide-react';
import React, { useEffect, useState } from 'react';

const TALLY_FORM_URL = 'https://tally.so/r/PdLgL0';
const TALLY_EMBED_URL = 'https://tally.so/embed/PdLgL0?alignLeft=1&hideTitle=1&transparentBackground=1&dynamicHeight=1';
const TOURNAMENT_ARTWORK = '/assets/landing/saca-tournament-august-2026-portrait.jpg';

const TallyFormSection: React.FC = () => {
    const [formOpen, setFormOpen] = useState(false);

    useEffect(() => {
        if (!formOpen) return undefined;

        const previousOverflow = document.body.style.overflow;
        const closeOnEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape') setFormOpen(false);
        };

        document.body.style.overflow = 'hidden';
        document.addEventListener('keydown', closeOnEscape);

        return () => {
            document.body.style.overflow = previousOverflow;
            document.removeEventListener('keydown', closeOnEscape);
        };
    }, [formOpen]);

    return (
        <section id="saca-form" className="scroll-mt-20 overflow-hidden bg-[#050806] py-12 md:py-20">
            <div className="mx-auto max-w-7xl px-4 md:px-10">
                <div className="grid overflow-hidden rounded-[24px] border border-[#58CF22]/40 bg-[#07110B] shadow-[0_30px_100px_rgba(0,0,0,0.45)] md:rounded-[36px] lg:grid-cols-[minmax(380px,0.86fr)_minmax(0,1.14fr)]">
                    <button
                        type="button"
                        onClick={() => setFormOpen(true)}
                        aria-label="Open SACA tournament registration"
                        className="group relative block overflow-hidden bg-black focus:outline-none focus:ring-4 focus:ring-inset focus:ring-[#58CF22]"
                    >
                        <img
                            src={TOURNAMENT_ARTWORK}
                            alt="SACA Football Tournament on 22 and 23 August 2026, with a £1,000 cash prize"
                            loading="lazy"
                            className="block aspect-[113/160] w-full object-cover transition duration-500 group-hover:scale-[1.015]"
                        />
                        <span className="absolute inset-x-5 bottom-5 hidden rounded-xl bg-black/80 px-5 py-4 text-center text-sm font-black uppercase tracking-[0.15em] text-white backdrop-blur-sm transition group-hover:bg-[#58CF22] group-hover:text-black lg:block">
                            Click to register
                        </span>
                    </button>

                    <div className="flex flex-col justify-center border-t border-white/10 px-6 py-10 md:px-10 md:py-14 lg:border-l lg:border-t-0 lg:px-14">
                        <p className="text-sm font-black uppercase tracking-[0.22em] text-[#58CF22] md:text-base">
                            SACA Football Tournament
                        </p>
                        <h2 className="mt-4 text-5xl font-black uppercase leading-[0.88] tracking-[-0.055em] text-white sm:text-6xl lg:text-7xl xl:text-8xl">
                            August <span className="text-[#58CF22]">2026</span>
                        </h2>

                        <div className="mt-8 grid grid-cols-2 gap-3 border-y border-white/15 py-6 text-white sm:max-w-xl sm:gap-6">
                            <div>
                                <p className="text-xs font-black uppercase tracking-[0.18em] text-white/50">Saturday</p>
                                <p className="mt-1 text-2xl font-black uppercase sm:text-3xl">22 August</p>
                                <p className="mt-1 font-bold text-[#58CF22]">9AM+</p>
                            </div>
                            <div className="border-l border-white/15 pl-4 sm:pl-6">
                                <p className="text-xs font-black uppercase tracking-[0.18em] text-white/50">Sunday</p>
                                <p className="mt-1 text-2xl font-black uppercase sm:text-3xl">23 August</p>
                                <p className="mt-1 font-bold text-[#58CF22]">9AM+</p>
                            </div>
                        </div>

                        <p className="mt-8 text-5xl font-black uppercase leading-[0.9] tracking-[-0.05em] text-white sm:text-6xl xl:text-7xl">
                            £1,000 <span className="text-[#58CF22]">Cash Prize</span>
                        </p>

                        <button
                            type="button"
                            onClick={() => setFormOpen(true)}
                            className="group mt-9 inline-flex min-h-16 w-full items-center justify-center gap-3 rounded-xl bg-[#58CF22] px-8 py-5 text-lg font-black uppercase tracking-[0.08em] text-black transition hover:bg-white focus:outline-none focus:ring-4 focus:ring-[#58CF22]/35 sm:w-fit sm:min-w-[250px]"
                        >
                            Sign Up Now
                            <ArrowRight className="transition-transform group-hover:translate-x-1" size={22} />
                        </button>
                    </div>
                </div>
            </div>

            {formOpen && (
                <div
                    className="fixed inset-0 z-[1000] flex items-end justify-center bg-black/85 p-0 backdrop-blur-sm md:items-center md:p-6"
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="tournament-signup-title"
                    onMouseDown={(event) => {
                        if (event.currentTarget === event.target) setFormOpen(false);
                    }}
                >
                    <div className="flex h-[94vh] w-full max-w-4xl flex-col overflow-hidden rounded-t-[24px] border border-white/10 bg-white shadow-2xl md:h-[90vh] md:rounded-[28px]">
                        <div className="flex items-center justify-between gap-4 bg-[#07110B] px-5 py-4 text-white md:px-7">
                            <div>
                                <p className="text-xs font-black uppercase tracking-[0.2em] text-[#58CF22]">August 2026</p>
                                <h3 id="tournament-signup-title" className="mt-1 text-xl font-black md:text-2xl">
                                    Register for the SACA Tournament
                                </h3>
                            </div>
                            <button
                                type="button"
                                onClick={() => setFormOpen(false)}
                                aria-label="Close tournament registration form"
                                className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-white/15 text-white transition hover:bg-white hover:text-black focus:outline-none focus:ring-2 focus:ring-[#58CF22]"
                            >
                                <X size={22} />
                            </button>
                        </div>

                        <iframe
                            src={TALLY_EMBED_URL}
                            title="SACA tournament registration form"
                            width="100%"
                            height="760"
                            frameBorder="0"
                            marginHeight={0}
                            marginWidth={0}
                            className="min-h-0 flex-1 bg-white"
                        >
                            Loading registration form...
                        </iframe>

                        <div className="border-t border-slate-200 bg-slate-50 px-5 py-3 text-center">
                            <a
                                href={TALLY_FORM_URL}
                                target="_blank"
                                rel="noreferrer"
                                className="inline-flex items-center gap-2 text-sm font-bold text-green-800 hover:text-green-600"
                            >
                                Open registration in a new tab <ExternalLink size={15} />
                            </a>
                        </div>
                    </div>
                </div>
            )}
        </section>
    );
};

export default TallyFormSection;
